import React, { useEffect, useState } from 'react';
import { useCallCenter } from '../providers/CallCenterProvider';

interface AgentStatusBadgeProps {
    /** Distance from the top of the widget's own container, in px — passed
     * in rather than hardcoded so it can clear TopBar's actual height
     * instead of guessing a fixed offset. */
    top: number;
}

const AgentStatusBadge: React.FC<AgentStatusBadgeProps> = ({ top }) => {
    const { savedAgentName, savedAgentComments, configuration } = useCallCenter();
    const [dismissed, setDismissed] = useState(false);

    // Dismissing is "got it, for now" — the next Save/Reset (a real change
    // to what's being tracked) always brings it back, in case it's covering
    // something the agent actually needs to see.
    useEffect(() => {
        setDismissed(false);
    }, [savedAgentName, savedAgentComments]);

    const isTracking =
        Boolean(savedAgentName.trim()) && Boolean(savedAgentComments.trim());

    if (dismissed) {
        return null;
    }

    return (
        <div
            role='status'
            aria-live='polite'
            style={{ top }}
            className={`absolute right-4 z-50 flex items-center gap-2 max-w-xs rounded-lg border-2 border-solid px-4 py-3 shadow-lg ${
                isTracking
                    ? 'bg-green-50 border-green-400 text-green-800'
                    : 'bg-amber-50 border-amber-400 text-amber-800'
            }`}
        >
            {isTracking ? (
                <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={2}
                    stroke='currentColor'
                    className='size-6 shrink-0'
                >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
                    />
                </svg>
            ) : (
                <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={2}
                    stroke='currentColor'
                    className='size-6 shrink-0'
                >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z'
                    />
                </svg>
            )}
            <span className='text-sm font-semibold'>
                {isTracking
                    ? configuration?.agentFieldType === 'email'
                        ? `You're tracking ${savedAgentComments} as ${savedAgentName}`
                        : `Hi ${savedAgentName}, you're tracking ${savedAgentComments}`
                    : 'No agent set, please save.'}
            </span>
            <button
                type='button'
                aria-label='Dismiss'
                onClick={() => setDismissed(true)}
                className='shrink-0 rounded-full p-0.5 hover:bg-black/10'
            >
                <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={2}
                    stroke='currentColor'
                    className='size-4'
                >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M6 18 18 6M6 6l12 12'
                    />
                </svg>
            </button>
        </div>
    );
};

export default AgentStatusBadge;
